/**
 * Formats a number into string representation in K, M, or plain integer.
 *
 * @param {number} value - The number to format.
 * @param {boolean} shouldRound - Indicates if rounding is required.
 * @returns {string | null} - The formatted number or null if invalid input.
 */
export const numberFormatter = (value: number, shouldRound = true) => {
  if (value < 0 || typeof value !== 'number') {
    return null;
  }

  if (shouldRound && Math.abs(value) > 999 && Math.abs(value) <= 999999) {
    const number = Math.abs(value) / 1000;
    return `${parseFloat(number.toFixed(1))}K`;
  }
  if (shouldRound && Math.abs(value) > 999999) {
    const number = Math.abs(value) / 1000000;

    return `${parseFloat(number.toFixed(1))}M`;
  }
  return Math.sign(value) * Math.abs(value);
};

/**
 * Formats a duration, given in minutes, to a human-readable string.
 *
 * @param {Function} t - Translation function.
 * @param {number | string} [countMinutesRaw] - The duration in minutes.
 * @param {string} [defaultValue] - The default string if the input is invalid.
 * @returns {string | null} - The formatted duration or default value.
 */
export const durationFormat = (t, countMinutesRaw?: number | string, defaultValue?: string) => {
  if (!countMinutesRaw) {
    return defaultValue ?? null;
  }
  let minutes: number;

  if (typeof countMinutesRaw === 'string') {
    minutes = parseFloat(countMinutesRaw);
  } else {
    minutes = countMinutesRaw;
  }
  if (minutes < 0) {
    return null;
  }
  const countHours = Math.floor(minutes / 60);
  const countMinutes = Math.floor(minutes % 60);
  const countSeconds = parseFloat(((minutes - Math.floor(minutes)) * 60).toFixed(2));

  const formatedTime: string[] = [];
  if (countHours > 0) {
    formatedTime.push(`${countHours + t('time.hours')}`);
  }
  if (countMinutes > 0) {
    formatedTime.push(`${countMinutes + t('time.minutes')}`);
  }
  if (countSeconds >= 1) {
    formatedTime.push(`${~~countSeconds + t('time.seconds')}`);
  } else if (formatedTime.length == 0) {
    formatedTime.push(`${countSeconds + t('time.seconds')}`);
  }

  return formatedTime.join(' ');
};

/**
 * Converts a normalized ratio (0..1) into a rounded integer percent (0..100).
 * Returns 0 for null input.
 */
export const ratioToPercent = (rate: number | null): number =>
  rate === null ? 0 : Math.round(rate * 100);

/**
 * Calculates the median of an array of numbers, ensuring a minimum return value.
 *
 * @param {number[]} array - The array of numbers.
 * @param {number} [minimum=0] - The minimum value to return.
 * @returns {number} - The calculated median or minimum value.
 */
export const getMedian = (array: number[], minimum = 0) => {
  let value = minimum;

  if (!array || !array.length) {
    return value;
  }
  const sorted = array.slice().sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    value = (sorted[middle - 1] + sorted[middle]) / 2;
  } else {
    value = sorted[middle];
  }
  return value < minimum ? minimum : value;
};
